import { UserRole } from '@/enums/user-role';
import { CurrentPerson } from '@/models/person';
import { TokenResponse } from '@/models/token-response';
import { YiviAuthenticationResult } from '@/models/yivi-authentication.model';
import { AuthenticationService } from '@/services/authentication.service';
import { TabStore } from '@/state/tab.store';
import { withStorageSync } from '@angular-architects/ngrx-toolkit';
import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

const STORAGE_KEY = 'authentication';

interface AuthenticationState {
  currentUser: CurrentPerson | null;
  accessToken: string | null;
  refreshToken: string | null;
  roles: UserRole[] | null;
}

const initialState: AuthenticationState = {
  currentUser: null,
  accessToken: null,
  refreshToken: null,
  roles: null,
};

export const AuthenticationStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  // Tokens live in sessionStorage, not localStorage: a session must not outlive the browser
  // tab it was created in, and a shared machine must not hand the next user a signed-in portal
  // (ILOPUMA-753).
  withStorageSync({
    key: STORAGE_KEY,
    storage: () => sessionStorage,
    select: ({ accessToken, refreshToken, roles }) => ({
      accessToken,
      refreshToken,
      roles,
    }),
  }),
  withComputed(({ roles }) => ({
    isPlatformAdmin: computed(() => {
      return roles()?.includes('PlatformAdmin');
    }),
  })),
  withComputed(({ accessToken, isPlatformAdmin, roles }) => ({
    isLoggedIn: computed(() => !!accessToken()),
    // Portal access is derived from the user's roles (which come from the JWT), NOT from a
    // person record. A platform admin - or any user holding a portal role - may legitimately
    // have no person yet (persons are org-managed and decoupled from the identity, ILOPUMA-626/627),
    // so requiring a current person here would lock them out of the portal.
    isPlatformPortalUser: computed(() => {
      const userRoles = roles() || [];
      return (
        isPlatformAdmin() ||
        userRoles.includes('AnyOrganisationAdmin') ||
        userRoles.includes('AnyOrganisationSafetyCoordinator') ||
        userRoles.includes('AnyProjectAdmin') ||
        userRoles.includes('AnyProjectSafetyCoordinator')
      );
    }),
  })),
  withComputed(({ currentUser, accessToken }, authenticationService = inject(AuthenticationService)) => ({
    // The menu must always show who is signed in and offer a way out. The name claims on the
    // access token are the identity itself and carry the self-chosen roepnaam when the user set
    // one (ILOPUMA-725), so they win over the org-managed person record; the person is only a
    // fallback for tokens minted without name claims.
    currentUserName: computed(() => {
      const token = accessToken();
      const tokenName = token ? authenticationService.fetchNameFromAccessToken(token) : null;
      if (tokenName) {
        return tokenName;
      }

      const person = currentUser();
      return person ? `${person.firstName} ${person.lastName}`.trim() : null;
    }),
  })),
  withMethods(({ accessToken, ...state }, authenticationService = inject(AuthenticationService)) => ({
    handleToken({ accessToken, refreshToken }: TokenResponse) {
      const roles = authenticationService.fetchRolesFromAccessToken(accessToken);
      patchState(state, { accessToken, refreshToken, roles });
    },
    isTokenExpired() {
      const token = accessToken();
      if (!token) {
        return true;
      }

      return authenticationService.isTokenExpired(token);
    },
  })),
  withMethods(
    (
      { refreshToken, ...state },
      authenticationService = inject(AuthenticationService),
      tabStore = inject(TabStore),
    ) => ({
      async login(username: string, password: string) {
        const response = await firstValueFrom(authenticationService.login({ username, password }));
        state.handleToken(response);
      },

      applyYiviResult(result: YiviAuthenticationResult) {
        if (result.status === 'authenticated' && result.tokens) {
          state.handleToken(result.tokens as TokenResponse);
          return;
        }

        // Registration is handled in the mobile app; the portal only accepts existing users.
        throw new Error('yivi_registration_required');
      },

      async refresh() {
        const token = refreshToken();
        if (!token) {
          throw new Error('No refresh token available');
        }

        const response = await firstValueFrom(authenticationService.refresh(token));
        state.handleToken(response);
      },

      async logout() {
        patchState(state, initialState);
        tabStore.reset();
      },

      async updateCurrentUser() {
        try {
          const user = await firstValueFrom(authenticationService.currentPerson());
          patchState(state, { currentUser: user });
        } catch (error) {
          // A failed person lookup must not silently destroy a valid session. Only a genuine
          // auth failure (401) clears the session; anything else - e.g. 422 (the user has no
          // person record yet) or a transient/server error - keeps the tokens and just leaves
          // the current person null.
          if (error instanceof HttpErrorResponse && error.status === 401) {
            patchState(state, initialState);
            tabStore.reset();
          } else {
            console.warn('Error fetching current user:', error);
            patchState(state, { currentUser: null });
          }
        }
      },
    }),
  ),
  withMethods((state) => ({
    async retrieveToken() {
      if (!state.isLoggedIn()) {
        return null;
      }

      if (!state.isTokenExpired()) {
        return state.accessToken();
      }

      try {
        await state.refresh();
        return state.accessToken();
      } catch (error) {
        console.error('Failed to retrieve token', error);
        await state.logout();
      }

      return null;
    },
  })),
  withHooks({
    onInit() {
      // Sessions used to be synced to localStorage. Anything left there survives the move and
      // would keep a refresh token on disk forever, so clear it out on the first load after
      // the switch.
      localStorage.removeItem(STORAGE_KEY);
    },
  }),
);
